/**
 * Cross-platform auth token storage.
 *
 * - Web: localStorage (synchronous).
 * - Capacitor native: @capacitor/preferences (async, encrypted on iOS Keychain
 *   when available via the plugin's defaults).
 *
 * The token is mirrored into a synchronous in-memory cache after `bootstrap()`,
 * so callers like `getAuthHeaders()` can stay synchronous regardless of platform.
 */
import { Preferences } from "@capacitor/preferences"
import { IS_MOBILE_BUILD } from "@/lib/capacitor"

const STORAGE_KEY = "sigmai_auth_token"

let memoryToken: string | null = null
let bootstrapped = false

type Listener = (token: string | null) => void
const listeners = new Set<Listener>()

function notify() {
  for (const listener of listeners) listener(memoryToken)
}

/** Subscribe to token changes. Returns an unsubscribe function. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

async function readFromBackingStore(): Promise<string | null> {
  if (IS_MOBILE_BUILD) {
    const { value } = await Preferences.get({ key: STORAGE_KEY })
    return value ?? null
  }
  if (typeof window === "undefined") return null
  return window.localStorage.getItem(STORAGE_KEY)
}

async function writeToBackingStore(token: string): Promise<void> {
  if (IS_MOBILE_BUILD) {
    await Preferences.set({ key: STORAGE_KEY, value: token })
    return
  }
  if (typeof window === "undefined") return
  window.localStorage.setItem(STORAGE_KEY, token)
}

async function removeFromBackingStore(): Promise<void> {
  if (IS_MOBILE_BUILD) {
    await Preferences.remove({ key: STORAGE_KEY })
    return
  }
  if (typeof window === "undefined") return
  window.localStorage.removeItem(STORAGE_KEY)
}

/**
 * Load the persisted token into the in-memory cache. Must be called once at
 * app startup before any API calls that rely on synchronous header access.
 */
export async function bootstrap(): Promise<string | null> {
  memoryToken = await readFromBackingStore()
  bootstrapped = true
  return memoryToken
}

/** Synchronous accessor backed by the in-memory cache. */
export function getToken(): string | null {
  return memoryToken
}

export function hasBootstrapped(): boolean {
  return bootstrapped
}

export async function setToken(token: string): Promise<void> {
  memoryToken = token
  await writeToBackingStore(token)
  notify()
}

export async function clearToken(): Promise<void> {
  memoryToken = null
  await removeFromBackingStore()
  notify()
}

export const authStorage = {
  bootstrap,
  getToken,
  setToken,
  clearToken,
  hasBootstrapped,
  subscribe,
}
